import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoginPage.css';
import { AuthContext } from '../context/AuthContext';
import { notification, Modal } from 'antd';
import axios from 'axios';
import config from '../config.json';

function LoginPage() {
  const [phoneNo, setPhoneNo] = useState('');
  const [password, setPassword] = useState('');
  const [setPasswordVisible, setSetPasswordVisible] = useState(false);
  const [setPasswordValue, setSetPasswordValue] = useState('');
  const [confirmSetPasswordValue, setConfirmSetPasswordValue] = useState('');
  const [pendingPhone, setPendingPhone] = useState('');
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Basic validation
    if (phoneNo.trim() === '' || password.trim() === '') {
      notification.error({
        message: 'Error',
        description: 'Please enter phone number and password.',
      });
      return;
    }

    if (phoneNo.length !== 9) {
      notification.error({
        message: 'Error',
        description: 'Please enter a valid 9-digit phone number.',
      });
      return;
    }

    // Format phone
    let formattedPhone = phoneNo.replace(/\D/g, ''); // remove non-digits
    if (formattedPhone.startsWith('0')) {
      formattedPhone = '211' + formattedPhone.substring(1);
    } else {
      formattedPhone = '211' + formattedPhone;
    }

    try {
      // First check if user exists
      const apiUrl = config.API_URL;
      const checkResponse = await axios.post(`${apiUrl}/auth/checkUser`, {
        phoneNo: formattedPhone,
      });

      if (checkResponse.data.exists === false) {
        notification.error({
          message: 'Error',
          description: 'User not found. Please register.',
        });
        return;
      }

      if (checkResponse.data.hasPassword === false) {
        setPendingPhone(checkResponse.data.phoneNumber);
        setSetPasswordVisible(true);
        return;
      }

      // User exists and has password, proceed with login
      const response = await axios.post(`${apiUrl}/auth/login`, {
        phoneNo: formattedPhone,
        password,
      });

      if (response.data.status === true) {
        notification.success({
          message: 'Success',
          description: 'Login successful!',
        });
        console.log(response.data);

        const userData = {
          whatsappNumber: response.data.data.whatsappNumber,
          fullName: response.data.data.user.fullName || '',
          accountBalance: response.data.data.user.accountBalance,
          creditLimit: response.data.data.user.creditLimit,
          status: response.data.data.user.status,
          active_accesskey: response.data.data.user.active_accesskey
        };

        // Log in the user
        login(userData);

        // Redirect to the main application page
        navigate('/airtime');
      } else {
        notification.error({
          message: 'Error',
          description: response.data.result,
          duration: 7,
        });
      }
    } catch (error) {
      // If the API request fails, show an error notification
      let errorMessage = error.response?.data?.result || error.response?.data?.message || 'Failed to login. Please try again.';
      notification.error({
        message: 'Error',
        description: errorMessage,
        duration: 7,
      });
      console.error('API request failed:', error);
    }
  };

  const handleSetPassword = async () => {
    if (!setPasswordValue.trim() || !confirmSetPasswordValue.trim()) {
      notification.error({
        message: 'Error',
        description: 'Please enter password and confirmation.',
      });
      return;
    }

    if (setPasswordValue !== confirmSetPasswordValue) {
      notification.error({
        message: 'Error',
        description: 'Passwords do not match.',
      });
      return;
    }

    try {
      const apiUrl = config.API_URL;
      const response = await axios.post(`${apiUrl}/auth/setPassword`, {
        phoneNo: pendingPhone,
        password: setPasswordValue,
      });

      if (response.data.status === true) {
        notification.success({
          message: 'Success',
          description: 'Password set successfully. Logging in...',
        });
        setSetPasswordVisible(false);

        // Now login
        const loginResponse = await axios.post(`${apiUrl}/auth/login`, {
          phoneNo: pendingPhone,
          password: setPasswordValue,
        });

        if (loginResponse.data.status === true) {
          const userData = {
            whatsappNumber: loginResponse.data.data.whatsappNumber,
            fullName: loginResponse.data.data.user.fullName || '',
            accountBalance: loginResponse.data.data.user.accountBalance,
            creditLimit: loginResponse.data.data.user.creditLimit,
            status: loginResponse.data.data.user.status,
            active_accesskey: loginResponse.data.data.user.active_accesskey
          };

          login(userData);
          navigate('/airtime');
        }
      } else {
        notification.error({
          message: 'Error',
          description: response.data.result,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to set password.',
      });
    }
  };

  return (
    <div className="login-container">
      <h1>Login</h1>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="phoneNo">Phone Number:</label>
          <div className="phone-input">
            <span className="prefix">+211</span>
            <input
              type="tel"
              id="phoneNo"
              value={phoneNo}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 9); // only digits, max 9
                setPhoneNo(value);
              }}
              placeholder="912345678"
              maxLength="9"
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
          />
        </div>
        <button type="submit">Login</button>
      </form>
      <p>Don't have an account? <a href="/register">Register</a></p>
      <Modal
        title="Set Password"
        visible={setPasswordVisible}
        onOk={handleSetPassword}
        onCancel={() => setSetPasswordVisible(false)}
        okText="Set Password"
      >
        <div className="form-group">
          <label htmlFor="setPassword">Enter your password:</label>
          <input
            type="password"
            id="setPassword"
            value={setPasswordValue}
            onChange={(e) => setSetPasswordValue(e.target.value)}
            placeholder="Enter password"
          />
        </div>
        <div className="form-group">
          <label htmlFor="confirmSetPassword">Confirm your password:</label>
          <input
            type="password"
            id="confirmSetPassword"
            value={confirmSetPasswordValue}
            onChange={(e) => setConfirmSetPasswordValue(e.target.value)}
            placeholder="Confirm password"
          />
        </div>
      </Modal>
    </div>
  );
}

export default LoginPage;
